
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useOpenHotelsWhitelabel } from '@/hooks/useOpenHotelsWhitelabel';
import { useAuthAction } from '@/hooks/useAuthAction';
import BookChoiceModal from '@/components/BookChoiceModal/BookChoiceModal';
import BookingSsoBlockedModal from '@/components/BookingModal/BookingSsoBlockedModal';
import { buildBookWithAdvisorTypeformUrl } from '@/lib/booking/bookWithAdvisorTypeformUrl';
import { openExternal } from '@/lib/platform';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_BOOK_CHOICE_MODAL_OPENED,
  EVENT_BOOK_CHOICE_OPTION_SELECTED,
} from '@/lib/analyticsEvents';

export type BookChoiceSurface =
  | 'header'
  | 'header_mobile'
  | 'explore_advisors_section'
  | 'mobile_tab_travel'
  | 'mobile_tab_plus'
  | 'itineraries_page'
  | 'navigation_menu';

interface BookChoiceContextValue {
  /** Opens the book choice modal after booking-gate auth (same as legacy header Book). */
  openBookChoice: (surface?: BookChoiceSurface) => void;
  isBookWhitelabelLoading: boolean;
}

const BookChoiceContext = createContext<BookChoiceContextValue | null>(null);

export function useBookChoice(): BookChoiceContextValue {
  const ctx = useContext(BookChoiceContext);
  if (!ctx) {
    throw new Error('useBookChoice must be used within BookChoiceProvider');
  }
  return ctx;
}

export function BookChoiceProvider({ children }: { children: React.ReactNode }) {
  const pathname = useLocation().pathname;
  const [searchParams] = useSearchParams();
  const { requireAuth } = useAuthAction();
  const {
    openHotelsWhitelabel,
    isOpening: isBookWhitelabelLoading,
    blockedLoginUrl: bookBlockedLoginUrl,
    clearBlockedState: clearBookBlockedState,
  } = useOpenHotelsWhitelabel();

  const [isBookChoiceOpen, setIsBookChoiceOpen] = useState(false);
  const [surface, setSurface] = useState<BookChoiceSurface>('header');

  const openBookChoice = useCallback(
    (from: BookChoiceSurface = 'header') => {
      requireAuth(
        'booking',
        () => {
          setSurface(from);
          setIsBookChoiceOpen(true);
          WebAnalytics.track(EVENT_BOOK_CHOICE_MODAL_OPENED, {
            surface: from,
            ...WebAnalytics.getReferringCreatorProps(),
          });
        },
        {
          title: 'Sign in to book hotels',
          description: 'Create an account or log in to browse and book hotels at exclusive rates.',
        },
      );
    },
    [requireAuth],
  );

  const closeBookChoice = useCallback(() => {
    setIsBookChoiceOpen(false);
  }, []);

  const handleSelectBookOnline = useCallback(() => {
    WebAnalytics.track(EVENT_BOOK_CHOICE_OPTION_SELECTED, {
      choice: 'online',
      surface,
      ...WebAnalytics.getReferringCreatorProps(),
    });
    setIsBookChoiceOpen(false);
    openHotelsWhitelabel();
  }, [openHotelsWhitelabel, surface]);

  const handleSelectBookWithAdvisor = useCallback(() => {
    WebAnalytics.track(EVENT_BOOK_CHOICE_OPTION_SELECTED, {
      choice: 'advisor',
      surface,
      ...WebAnalytics.getReferringCreatorProps(),
    });
    setIsBookChoiceOpen(false);
    const url = buildBookWithAdvisorTypeformUrl(pathname, searchParams);
    if (typeof window !== 'undefined') {
      void openExternal(url);
    }
  }, [pathname, searchParams, surface]);

  const value = useMemo(
    () => ({
      openBookChoice,
      isBookWhitelabelLoading,
    }),
    [openBookChoice, isBookWhitelabelLoading],
  );

  return (
    <BookChoiceContext.Provider value={value}>
      {children}
      <BookChoiceModal
        isOpen={isBookChoiceOpen}
        onClose={closeBookChoice}
        onSelectBookOnline={handleSelectBookOnline}
        onSelectBookWithAdvisor={handleSelectBookWithAdvisor}
      />
      <BookingSsoBlockedModal
        isOpen={!!bookBlockedLoginUrl}
        onClose={clearBookBlockedState}
        loginUrl={bookBlockedLoginUrl}
      />
    </BookChoiceContext.Provider>
  );
}
